import React, { useContext } from 'react'
import moment from 'moment-timezone'
import { Box, Typography } from '@mui/material'
import { LocalizationContext } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, TimeSource, timeSourceTitle } from '../../../utils/timeSource'

export const NA = '—'

export interface FullTimeline {
  ordered_at: string | null
  preparation_started_at: string | null
  preparation_finished_at: string | null
  picked_up_at: string | null
}

export const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return NA
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`
}

export const TimelineRow = ({
  label,
  value,
  offsetSeconds,
  source
}: { label: string; value: string | null; offsetSeconds: number | null; source?: TimeSource }) => {
  // The sign is carried here rather than inside formatDuration, which formats a magnitude.
  // An offset can genuinely come out negative: "Preparation started" is measured on the robot's
  // clock while "Ordered at" is on ours, so a robot running behind subtracts to less than zero.
  // Showing that as a negative offset is at least truthful; a "+" in front of it was not.
  const offset = offsetSeconds == null
    ? null
    : `${offsetSeconds < 0 ? '-' : '+'}${formatDuration(Math.abs(offsetSeconds))}`

  return (
    <div className='timeline-row'>
      <span className='timeline-label'>{label}</span>
      <span className='timeline-value' title={source && timeSourceTitle(value, source)}>{value || NA}</span>
      {offset && <span className='timeline-offset'>({offset})</span>}
    </div>
  )
}

// Full meal timeline. Callers can append their own rows via children - the meal page adds
// the deposit weight, which belongs in the block but is not a timestamp.
const Timeline = ({
  timeline,
  title = 'Timeline',
  children
}: { timeline: FullTimeline; title?: string; children?: React.ReactNode }) => {
  const localization = useContext(LocalizationContext)
  const fmt = (value: string | null) => formatTenantDateTime(value, localization)
  const orderedAtMoment = timeline.ordered_at ? moment(timeline.ordered_at) : null
  const offset = (iso: string | null) => {
    if (!iso || !orderedAtMoment) return null
    return moment(iso).diff(orderedAtMoment, 'seconds')
  }

  return (
    <Box className='order-show-timeline'>
      {title && <Typography variant='subtitle1' className='timeline-title'>{title}</Typography>}
      <TimelineRow label='Ordered at' value={fmt(timeline.ordered_at)} offsetSeconds={null} source={TIME_SOURCE.app} />
      <TimelineRow label='Preparation started' value={fmt(timeline.preparation_started_at)} offsetSeconds={offset(timeline.preparation_started_at)} source={TIME_SOURCE.robot} />
      <TimelineRow label='Placed in locker' value={fmt(timeline.preparation_finished_at)} offsetSeconds={offset(timeline.preparation_finished_at)} source={TIME_SOURCE.app} />
      <TimelineRow label='Picked up' value={fmt(timeline.picked_up_at)} offsetSeconds={offset(timeline.picked_up_at)} source={TIME_SOURCE.app} />
      {children}
    </Box>
  )
}

export default Timeline
